import { Stack, Paper, Group, Text, rem } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { featureCollection } from '@turf/turf';
import { MapEvent } from 'mapbox-gl';
import { FC, useState } from 'react';

import { ManageCarbonSamplingPlanForm } from '../../components/forms/manage-carbon-sampling-plan-form';
import { ManageDensitySamplingPlanForm } from '../../components/forms/manage-density-sampling-plan-form';
import { SamplePopup, SamplePopupData } from '../../components/sample-popup';
import { useSamplingQueries } from '../../data-access/queries/sampling';
import { AnalysisType } from '../../data-access/types/analysis-type';
import { flattenSamples } from '../../utils/flatten-samples';

import {
  PfMap,
  SamplesLayerProps,
  SamplesLayers,
  ZoningLayer,
  zoomOnFeature,
} from '@map';
import { FullMainHeight } from '@ui';

export const SamplingPlanPage: FC = () => {
  const { id } = useParams({
    from: '/_app/_admin/sampling-plans_/$id',
  });
  const navigate = useNavigate({
    from: '/sampling-plans/$id',
  });
  const [popupData, setPopupData] = useState<SamplePopupData | null>(null);

  const samplingQueries = useSamplingQueries();
  const { data } = useSuspenseQuery(samplingQueries.plans.detail(Number(id)));

  const samples = flattenSamples(data.samples);

  const handleMapLoad = (map: MapEvent) => {
    zoomOnFeature({
      map: map.target,
      geojson: data.map,
    });
  };

  const handleNavigateBack = () => {
    navigate({
      to: '/sampling-plans',
    });
  };

  const handleMarkerClick: SamplesLayerProps['onClick'] = ({
    id: featureId,
    lngLat,
  }) => {
    const point = samples.find((f) => f.id === featureId);
    setPopupData({
      longitude: lngLat.lng,
      latitude: lngLat.lat,
      sampleId: point?.properties.sampleId ?? null,
      pointId: point?.id ?? featureId ?? null,
      fieldId: point?.properties.field_id ?? null,
    });
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.FlexContent>
        <FullMainHeight.Sidebar>
          <Stack h="100%">
            <Paper>
              <Group>
                <Stack
                  flex="1"
                  gap={0}
                  h={rem(36)}
                  justify="center"
                  style={{
                    flexWrap: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  <Text fw="bold" lh={1} size="xl" truncate="end">
                    {data.name}
                  </Text>
                  <Text lh={1} size="md" truncate="end">
                    {data.account.first_name} {data.account.last_name}
                  </Text>
                </Stack>
              </Group>
            </Paper>
            <Paper flex="1">
              {data.analysis_type === AnalysisType.CARBON ? (
                <ManageCarbonSamplingPlanForm
                  analysisType={data.analysis_type}
                  fieldTeledecId={data.field_teledec_id}
                  ilotId={data.ilot_id}
                  onBack={handleNavigateBack}
                  planId={Number(id)}
                />
              ) : (
                <ManageDensitySamplingPlanForm
                  analysisType={data.analysis_type}
                  fieldTeledecId={data.field_teledec_id}
                  ilotId={data.ilot_id}
                  onBack={handleNavigateBack}
                  planId={Number(id)}
                />
              )}
            </Paper>
          </Stack>
        </FullMainHeight.Sidebar>
        <PfMap onLoad={handleMapLoad}>
          {samples.length ? (
            <SamplesLayers
              data={featureCollection(samples)}
              onClick={handleMarkerClick}
              size={30}
              withLabel
            />
          ) : null}
          <ZoningLayer data={data.map} />
          {popupData && (
            <SamplePopup data={popupData} onClose={() => setPopupData(null)} />
          )}
        </PfMap>
      </FullMainHeight.FlexContent>
    </FullMainHeight.Root>
  );
};
